import {
  compareExactDecimal,
  divideExactDecimalAtScale,
  exactDecimalFromText,
  exactDecimalToText,
  type ExactDecimal,
} from './exact-decimal';

const MILLION: ExactDecimal = { coefficient: 1_000_000n, scale: 0 };
const THOUSAND: ExactDecimal = { coefficient: 1_000n, scale: 0 };
const DOLLAR: ExactDecimal = { coefficient: 1n, scale: 0 };
type CompactPrecision = 1 | 2;

/** Server display only: exact half-up, millions to one decimal by default, thousands/dollars whole. */
export function formatCompactMoney(value: string | number, precision: CompactPrecision = 1,
  minimumUnit: 'dollar' | 'thousand' | 'million' = 'dollar'): string {
  const parse = (source: string | number): ExactDecimal => {
    const text = typeof source === 'number' ? source.toString() : source.trim();
    // Published text is plain decimal; numeric callers may stringify with an exponent.
    if (typeof source === 'string' && !/^-?\d+(?:\.\d+)?$/u.test(text))
      throw new Error(`Not a published decimal: ${text}`);
    const parsed = exactDecimalFromText(text);
    if (!parsed) throw new Error(`Not a published decimal: ${text}`);
    return parsed;
  };
  const decimal = parse(value);
  const magnitude = { ...decimal,
    coefficient: decimal.coefficient < 0n ? -decimal.coefficient : decimal.coefficient };
  const divisor = minimumUnit === 'million' || compareExactDecimal(magnitude, MILLION) >= 0 ? MILLION
    : minimumUnit === 'thousand' || compareExactDecimal(magnitude, THOUSAND) >= 0 ? THOUSAND : DOLLAR;
  const digits = divisor === MILLION ? precision : 0;
  const rounded = divideExactDecimalAtScale(magnitude, divisor, digits);
  const render = (): string => {
    if (rounded.coefficient === 0n && minimumUnit !== 'million') return minimumUnit === 'thousand' ? '$0K' : '$0';
    const [whole, fraction] = exactDecimalToText(rounded).split('.');
    const sign = decimal.coefficient < 0n ? '−' : '';
    const suffix = divisor === MILLION ? 'M' : divisor === THOUSAND ? 'K' : '';
    return `${sign}$${BigInt(whole).toLocaleString('en-US')}${fraction === undefined ? '' : `.${fraction}`}${suffix}`;
  };
  return render();
}

/** Exact cents to plain decimal dollars, without a floating-point intermediate. */
export function centsToDollarText(cents: bigint): string {
  const sign = cents < 0n ? '-' : '';
  const magnitude = cents < 0n ? -cents : cents;
  return `${sign}${magnitude / 100n}.${(magnitude % 100n).toString().padStart(2, '0')}`;
}

/** Published decimal dollars to cents, half away from zero below one cent. */
export function decimalToCents(text: string): bigint | null {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/u.exec(text.trim());
  if (!match) return null;
  const [, sign, whole, fraction = ''] = match;
  const padded = `${fraction}000`;
  let cents = BigInt(whole) * 100n + BigInt(padded.slice(0, 2));
  if (Number(padded[2]) >= 5) cents += 1n;
  return sign === '-' ? -cents : cents;
}
